import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  ContentBody,
  ContentContainer,
  ContentHeader,
  Links,
  SocialMedia,
  ClipEntry,
  ClipText,
  MetaRow,
  HdBanner,
  HdBadge,
  HdBannerText,
  HdBannerLink,
  ViralSection,
  ViralHeader,
  ViralTitleRow,
  ViralFeatureGrid,
  ViralFeatureCard,
  ViralFeatureCardWrap,
  ViralFeatureBadge,
} from "./shorts-components";
import Typography from "../../common-components/Typography";
import vmTheme from "../../constants/theme";
import { font } from "../../constants/font";
import content from "../../constants/content";
import Action from "../../common-components/Action";
import CustomTooltip from "../../common-components/CustomTooltip";
import openUrl from "../../helper/openUrl";
import { API_URL, STATIC_PATH, API, SITE_URL, ENVIRONMENT } from "../../constants/config";
import Export from "../modal/export";
import useApi from "../../helper/hooks/useApi";
import { secondsToMinute } from "../timeline/timeline-helper";
import { Divider, FlexDiv } from "./shorts-list";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import { PLAN, VIRAL_FEATURES } from "../../constants";
import { playPausePlayer, setPlayAll } from "../../redux/actions/appUtils";
import ShortsFeedbackButtons from "./shorts-feedback-buttons";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";

const { OPEN_SHORT_PRESET, SET_ACTIVE_PLAYER } = contextActionType;

const PRICING_MODAL_ARGS = { type: "longtoshort", source: "free-upgrade-shorts" };

const ShortsContent = ({
  shortData,
  inModal = false,
  showPricingModal,
  feedbackType,
  onLike,
  onDislike,
}) => {
  const dispatch = useDispatch();
  const { post } = useApi();
  const theme = useSelector((state) => state.app.get("theme"));
  const t1 = useSelector((state) => state.app.get("t1"));
  const projectDetails = useSelector((state) => state.projectDetails);
  const { permission, projectId, plan } = useSelector((state) => state.userDetails);
  const { setShortsContext } = useShortsContext();
  const hasSubtitle = !!projectDetails.get("localSubtitle").size;

  const [isModal, setIsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const closeModalExport = () => setIsModal(false);

  const pausePlayer = useCallback(() => {
    const start = shortData.get("start");
    let playhead = start;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    dispatch(setPlayAll(false, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
  }, [dispatch, shortData, t1, setShortsContext]);

  const openPreset = () => {
    if (hasSubtitle) {
      pausePlayer();
      setShortsContext(OPEN_SHORT_PRESET, true);
    }
  };

  const trackUpgrade = useCallback((source) => {
    trackerEvents(TRACKERS.UPGRADE_FROM_EDITOR, {
      projectId,
      shortId: shortData.get("id"),
      source,
    });
  }, [projectId, shortData]);

  const handleFeatureClick = (e, feature) => {
    e.stopPropagation();
    if (feature.disabled) return;

    pausePlayer();

    if (feature.opensPreset) {
      if (hasSubtitle) {
        openPreset();
      } else if (showPricingModal) {
        trackUpgrade(feature.upgradeSource);
        showPricingModal({
          type: feature.upgradeType,
          source: feature.upgradeSource,
        });
      }
      return;
    }

    if (!showPricingModal) return;
    trackUpgrade(feature.upgradeSource);
    showPricingModal({
      type: feature.upgradeType,
      source: feature.upgradeSource,
    });
  };

  const openNewProject = useCallback((shortId, isExport = false) => {
    pausePlayer();
    if (isExport) {
      trackerEvents(TRACKERS.EXPORT_FROM_EDITOR, { projectId, shortId });
    } else {
      trackerEvents(TRACKERS.SHORTS_EDIT_AS_PROJECT_FLOW_EDITING_SCREEN, {
        projectId,
        shortId,
      });
    }
    let pathName = "";
    if (isExport) {
      pathName = "render/";
      setIsExportLoading(true);
    } else {
      pathName = ENVIRONMENT === "LOCAL" ? "" : "editproject/";
      setIsLoading(true);
    }
    post(`${API_URL}${API.SHORTS.CREATE}`, { projectId, shortId, isExport }).then((res) => {
      if (!res.error) {
        openUrl(`${SITE_URL}${pathName + res.projectId}`);
        setIsLoading(false);
        setIsExportLoading(false);
      }
    });
  },
    [projectId, post, pausePlayer]
  );

  const truncate = (str, length) => {
    if (str.length > length) {
      return `${str.substr(0, length)}...`;
    }
    return str;
  };

  const clips = shortData.get("clips");
  const hasClips = clips && clips.size > 0;

  const renderBody = () => {
    if (hasClips) {
      return clips.map((clip, index) => {
        const startTime = clip.get("startTime");
        const endTime = clip.get("endTime");
        const clipText = clip.get("text") || clip.get("transcript") || "";
        return (
          // eslint-disable-next-line react/no-array-index-key
          <ClipEntry key={`${index}-${startTime}`} >
            <ClipText color={vmTheme[theme].subtitleTextColor}>
              <span style={{ font: font.boldBase_21 }}>[{secondsToMinute(startTime)} - {secondsToMinute(endTime)}]</span> {clipText}
            </ClipText>
          </ClipEntry>
        );
      });
    }
    return (
      <ClipEntry>
        <span style={{ font: font.boldBase_21 }}>[{secondsToMinute(shortData.get("start"))} - {secondsToMinute(shortData.get("end"))}]</span>
        <Typography
          innerContent={shortData.get("phrase") || ""}
          color={vmTheme[theme].subtitleTextColor}
          font={font.normal_21}
          enableTrim={false}
        />
      </ClipEntry>
    );
  };

  return (
    <ContentContainer>
      <ContentHeader>
        {(shortData.get("category") || shortData.get("confidenceScore")) && (
          <MetaRow>
            {shortData.get("category") && (
              <Typography innerContent={shortData.get("category").charAt(0).toUpperCase() + shortData.get("category").slice(1)} font={font.medium_12} color={'#5B5F70'} padding={'3px 8px'} 
                textStyles={{ background: '#EBECF0', borderRadius: '50px' }}
              />
            )}
          </MetaRow>
        )}
        <Export
          modalHandler={closeModalExport}
          isShowModal={isModal}
          projectTitle={truncate(shortData.get("title"), 40)}
          onCloseModal={() => setIsModal(false)}
          shortId={shortData.get("id")}
        />
      </ContentHeader>
      <FlexDiv justifyContent="space-between" gap="12px">
        <Typography
          content={shortData.get("title")}
          font={font.normal_600_16}
          color={vmTheme[theme].shortsSubText}
          padding="0px 0px 0px"        
          enableTrim={false}
          textStyles={{ flex: 1, minWidth: 0, lineHeight: '1.4' }}
        />
        {inModal && (
          <ShortsFeedbackButtons
            feedbackType={feedbackType}
            tooltipIdSuffix={shortData.get("id")}
            tooltipContext="modal"
            variant="onLight"
            onLike={onLike}
            onDislike={onDislike}
          />
        )}
      </FlexDiv>
      <FlexDiv style={{ marginTop: '16px', paddingBottom: '16px' }}>
        {shortData.get("confidenceScore") && (
          <Typography
            content={`${Math.round(shortData.get("confidenceScore"))}%`}
            font={font.bold_24_900}
            enableTrim={false}
            color={vmTheme[theme].shortsColor}
            padding={'3px 14px'}
            textStyles={{
              borderRadius: '100px',
              background: '#DBF8ED'
            }}
          />
        )}
        <FlexDiv justifyContent="flex-end" gap="12px">
          {plan !== PLAN.LITE && <CustomTooltip
            tooltipId="shorts-subtitle-btn-tt"
            dataTooltip={content.SUBTITLE_STYLE}
            font={font.normal_medium_12}
            tooltipPosition="top"
            delayVisibilityBy={0}
          >
            <Action
              data-tooltip-id="shorts-subtitle-btn-tt"
              text={''}
              font={font.mediumBase_21}
              className="subtitle-btn"
              marginTop="0px"
              fontColor={vmTheme[theme].shortsSubText}
              marginBottom="0px"
              width="52px"
              height={"36px"}
              borderRadius={"8px"}
              borderWidth={"1px"}
              customBeforeIcon={`${STATIC_PATH}subtitle.svg`}
              borderColor={vmTheme[theme].shortsBorderColor}
              onClick={() => openPreset()}
              isDisabled={!hasSubtitle}
            />
          </CustomTooltip>}
          {permission !== 0 && (
            <CustomTooltip
              tooltipId="shorts-edit-project-tt"
              font={font.normal_medium_12}
              dataTooltip={content.EDIT_AS_PROJECT}
              tooltipPosition="top"
              delayVisibilityBy={0}
            >
              <Action
                data-tooltip-id="shorts-edit-project-tt"
                text={''}
                font={font.mediumBase_21}
                className="edit-project"
                marginTop="0px"
                fontColor={vmTheme[theme].shortsSubText}
                marginBottom="0px"
                width="52px"
                height={"36px"}
                borderRadius={"8px"}
                borderWidth={"1px"}
                customBeforeIcon={`${STATIC_PATH}vm-cut.svg`}
                iconWidth="20px"
                iconHeight="20px"
                borderColor={vmTheme[theme].shortsBorderColor}
                onClick={() => openNewProject(shortData.get("id"))}
                isLoading={isLoading}
              />
            </CustomTooltip>
          )}
          {permission !== 0 && (
            <>
              <Divider />
              <Action
                text={content.EXPORT}
                font={font.mediumBase_21}
                className="export-project"
                marginTop="0px"
                fontColor={vmTheme[theme].shortsSecondaryColor}
                marginBottom="0px"
                width="99px"
                height={"36px"}
                borderRadius={"8px"}
                background={vmTheme[theme].shortsBtnBgColor}
                onClick={() => openNewProject(shortData.get("id"), true)}
                isLoading={isExportLoading}
              />
            </>)}
        </FlexDiv>
      </FlexDiv>

      <ContentBody $inModal={inModal}>
        {plan === PLAN.LITE && <div>
          <HdBanner>
            <HdBadge>
              <img src={`${STATIC_PATH}hd-icon.svg`} alt="HD" />
            </HdBadge>
            <HdBannerText>
              <FormattedMessage id={content.SHORTS_FREE_EXPORT_NOTE} />
              <HdBannerLink onClick={() => {
                pausePlayer();
                trackUpgrade(PRICING_MODAL_ARGS.source);
                showPricingModal(PRICING_MODAL_ARGS);
              }}>
                <FormattedMessage id={content.SHORTS_GO_HD} />
              </HdBannerLink>
            </HdBannerText>
          </HdBanner>

          <ViralSection>
            <ViralHeader>
              <ViralTitleRow>
                <img src={`${STATIC_PATH}bolt.svg`} alt="Viral" />
                <Typography
                  content={content.SHORTS_MAKE_IT_VIRAL}
                  font={font.boldBase_16}
                  color="#5028FF"
                  enableTrim={false}
                />
              </ViralTitleRow>
            </ViralHeader>
            <ViralFeatureGrid>
              {VIRAL_FEATURES.map((feature) => {
                const tooltipId = `shorts-viral-${feature.id}-tt`;
                return (
                  <CustomTooltip
                    key={feature.id}
                    tooltipId={tooltipId}
                    dataTooltip={feature.description}
                    tooltipPosition="top"
                    font={font.normal_14}
                    delayVisibilityBy={0}
                  >
                    <ViralFeatureCardWrap>
                      {feature.badgeVariant && (
                        <ViralFeatureBadge $variant={feature.badgeVariant}>
                          {feature.badgeVariant === "comingSoon" ? (
                            <Typography
                              content={feature.badge}
                              font={font.mediumMicro_10}
                              color="#ffffff"
                              enableTrim={false}
                            />
                          ) : (
                            <img
                              src={`${STATIC_PATH}${feature.badgeVariant}.svg`}
                              alt=""
                            />
                          )}
                        </ViralFeatureBadge>
                      )}
                      <ViralFeatureCard
                        type="button"
                        disabled={feature.disabled}
                        data-tooltip-id={tooltipId}
                        onClick={(e) => handleFeatureClick(e, feature)}
                      >
                        <img src={`${STATIC_PATH}${feature.icon}`} alt="" />
                        <Typography
                          content={feature.label}
                          font={font.medium_12}
                          color={vmTheme[theme].shortsSubText}
                          enableTrim={false}
                          align="center"
                          cursor={feature.disabled ? "default" : "pointer"}
                        />
                      </ViralFeatureCard>
                    </ViralFeatureCardWrap>
                  </CustomTooltip>
                );
              })}
            </ViralFeatureGrid>
          </ViralSection>
        </div>}

        {shortData.get("socialMedia") && (
          <SocialMedia isBorder={true} style={{ marginTop: '16px' }}>
            <Typography
              content={content.SUITABLE_FOR}
              width={"115px"}
              display={"block"}
              color={vmTheme[theme].sbSecondaryColor}
            />
            <FlexDiv justifyContent="flex-end" gap="10px">
              {shortData.get("socialMedia").map((icon) => {
                return (
                  <Links key={icon}>
                    <img
                      src={`${STATIC_PATH + icon.split(" ")[0].toLowerCase()}.svg`}
                      alt={icon}
                    />
                  </Links>
                );
              })}
            </FlexDiv>
          </SocialMedia>
        )}

        <Typography
          content={content.TRANSCRIPT}
          font={font.normalMini_14_700}
          color={vmTheme[theme].subtitleTextColor}
          padding="16px 0px"
          display="block"
          enableTrim={false}
        />
        {renderBody()}
      </ContentBody>
    </ContentContainer>
  );
};

ShortsContent.propTypes = {
  shortData: PropTypes.object,
  inModal: PropTypes.bool,
  showPricingModal: PropTypes.func,
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
};

export default React.memo(ShortsContent, (prevProps, nextProps) => {
  return (
    prevProps.shortData === nextProps.shortData
    && prevProps.feedbackType === nextProps.feedbackType
  );
});
